import type {
  ImpactPackageEstimateBasis,
  ImpactPackageEstimateKeywordChange,
  ImpactPackageEstimateKeywordPlace
} from './impact-brief-types';

/**
 * The traffic-estimate line on a site without Search Console (TECHNICAL_DEBT §0cd, quality sprint
 * 6 B). The owner's rule is that the estimate is never hidden; graders read it as a measured count
 * with nothing to act on. So the line says the change in words, then one cause (or that none is on
 * record), then what the estimate rests on. A position for the comparison search is never stated
 * without Google's stored results for it: where the two disagree, Google's is said, with its date.
 * One function, read by `/review`'s header and by the export (and so the MCP).
 */

function quoted(text: string): string {
  return `“${text.replace(/\s+/g, ' ').trim()}”`;
}

function count(n: number): string {
  return n.toLocaleString('en-US');
}

/** Whole visits from 10 up, one decimal below, as the export rounds them. */
function visits(n: number): string {
  const abs = Math.abs(n);
  return abs >= 10 ? count(Math.round(abs)) : String(Math.round(abs * 10) / 10);
}

function at(p: ImpactPackageEstimateKeywordPlace): string {
  if (p.kind === 'ranked') return `#${p.position}`;
  if (p.kind === 'below') return `#${p.position} or lower`;
  return 'nowhere';
}

/** How a ranked keyword moved between the two crawls' lists, in a few words. */
function moved(change: ImpactPackageEstimateKeywordChange): string {
  const a = change.previous;
  const b = change.current;
  if (a.kind === 'ranked' && b.kind === 'absent') return `dropped out from #${a.position}`;
  if (a.kind === 'absent' && b.kind === 'ranked') return `came in at #${b.position}`;
  if (a.kind !== 'absent' && b.kind !== 'absent') {
    if (a.kind === b.kind && a.position === b.position) {
      const va = change.previousSearchVolume;
      const vb = change.currentSearchVolume;
      return va != null && vb != null && va !== vb
        ? `held ${at(b)} while its searches went from ${count(va)} to ${count(vb)} a month`
        : `held ${at(b)}`;
    }
    const worse = b.position > a.position || (b.kind === 'below' && b.position >= a.position);
    return `${worse ? 'fell' : 'rose'} from ${at(a)} to ${at(b)}`;
  }
  return `went from ${at(a)} to ${at(b)}`;
}

function searches(change: ImpactPackageEstimateKeywordChange): string {
  const v = change.currentSearchVolume ?? change.previousSearchVolume;
  return v == null ? '' : ` (${count(v)} searches a month)`;
}

/** Google's stored results against the place the estimate counts the comparison search at. */
function storedResultsCheck(basis: ImpactPackageEstimateBasis): string | null {
  const cmp = basis.comparison;
  if (!cmp?.conflict || !cmp.live) return null;
  const when = cmp.live.fetchedOn
    ? ` (fetched ${cmp.live.fetchedOn}${cmp.live.afterCrawl ? ', after this crawl' : ''})`
    : '';
  const google = `Google's results for ${quoted(cmp.query)}${when}`;
  const now = cmp.change?.current ?? { kind: 'absent' as const };
  // The comparison search's own move is the cause: say where its ranked keywords had it, once.
  const wasRanked = cmp.change?.previous.kind === 'ranked' ? cmp.change.previous.position : null;
  const had =
    basis.causeOnRecord === 'comparison' && wasRanked != null ? ` (it was #${wasRanked} at the previous crawl)` : '';
  if (cmp.conflict === 'outside') {
    return `${google} do not show the page in their top ${cmp.live.depth}; the estimate counts it at ${at(now)}.`;
  }
  const shown =
    now.kind === 'absent'
      ? `${google} show the page at #${cmp.live.position}, a search its ranked keywords ${wasRanked != null ? 'no longer include' : 'do not include'}${had}`
      : `${google} show the page at #${cmp.live.position}, not the estimate's ${at(now)}${had}`;
  return cmp.conflict === 'higher' ? `${shown}, so the estimate is likely too low.` : `${shown}.`;
}

function cause(basis: ImpactPackageEstimateBasis): string | null {
  const cmp = basis.comparison;
  if (basis.causeOnRecord === 'comparison' && cmp?.change) {
    // Its move was said, against Google's results, by the check: name it as the cause.
    if (cmp.conflict) return `That ${basis.direction === 'decay' ? 'loss' : 'gain'} is the cause on record.`;
    return `Among its ranked keywords, its comparison search ${quoted(cmp.query)} ${moved(cmp.change)}.`;
  }
  if (basis.causeOnRecord === 'keyword' && basis.largestChange) {
    const k = basis.largestChange;
    return `Largest change on record: ${quoted(k.keyword)}${searches(k)} ${moved(k)}.`;
  }
  if (!basis.keptListsExplain) {
    return `No cause on record: most of it is outside the ${basis.keptPerCrawl} keywords we keep.`;
  }
  return 'No change among its ranked keywords on record explains it.';
}

function restsOn(basis: ImpactPackageEstimateBasis): string | null {
  const { previous, current } = basis.rankedKeywords;
  const before = previous == null || previous === current ? '' : ` (${count(previous)} at the previous crawl)`;
  if (current == null) return null;
  if (current === 0) return `No ranked keywords are on record now${before}, so it is 0.`;
  // Counts only: a position here would be a ranked-keyword position stated without Google's check.
  return `It rests on ${count(current)} ranked ${current === 1 ? 'keyword' : 'keywords'}${before}.`;
}

/**
 * The whole line after the page's path, starting lower-case ("fell from about 19 to 17 visits a
 * month, …"): the change, Google's stored results where they disagree with the estimate, the cause
 * or that none is on record, and what the estimate rests on.
 */
export function estimateMovementLine(basis: ImpactPackageEstimateBasis): string {
  const verb = basis.direction === 'decay' ? 'fell' : 'rose';
  const head = `${verb} from about ${visits(basis.visits.previous)} to ${visits(basis.visits.current)} visits a month, estimated from our crawl, not Search Console.`;
  return [head, storedResultsCheck(basis), cause(basis), restsOn(basis)].filter(Boolean).join(' ');
}

/** The same line as a sentence of its own, for `/review`'s header. */
export function estimateMovementSentence(basis: ImpactPackageEstimateBasis): string {
  const line = estimateMovementLine(basis);
  return line.charAt(0).toUpperCase() + line.slice(1);
}
